import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Lock, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al restablecer contraseña');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#A65C5C]">Token Inválido</h2>
          <p className="mt-2 text-[#6A736B]">El link de reseteo no es válido</p>
          <Link to="/login">
            <Button className="mt-4 bg-[#2B4C3B] hover:bg-[#3A604D] text-white">
              Volver al login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div
        className="hidden lg:block lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/30887567/pexels-photo-30887567.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
        }}
      >
        <div className="absolute inset-0 bg-[#2B4C3B]/10 backdrop-blur-sm"></div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#FDFBF7]">
        <div className="w-full max-w-md space-y-8">
          {!success ? (
            <>
              <div className="text-center">
                <h1 className="text-4xl font-bold text-[#2B4C3B] tracking-tight">Nueva Contraseña</h1>
                <p className="mt-2 text-[#6A736B]">Ingresa tu nueva contraseña</p>
              </div>

              <form onSubmit={handleSubmit} className="mt-8 space-y-6" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="newPassword" className="text-[#1A1C18]">Nueva Contraseña</Label>
                  <Input
                    id="newPassword"
                    data-testid="new-password-input"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="mt-1 bg-transparent border-[#E8E5DD] focus:ring-2 focus:ring-[#2B4C3B] focus:border-transparent"
                    placeholder="••••••••"
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-[#1A1C18]">Confirmar Contraseña</Label>
                  <Input
                    id="confirmPassword"
                    data-testid="confirm-password-input"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="mt-1 bg-transparent border-[#E8E5DD] focus:ring-2 focus:ring-[#2B4C3B] focus:border-transparent"
                    placeholder="••••••••"
                  />
                </div>

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="submit-reset-password"
                  disabled={loading}
                  className="w-full bg-[#2B4C3B] hover:bg-[#3A604D] text-white rounded-lg h-11 transition-colors duration-200"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Restableciendo...
                    </>
                  ) : (
                    <>
                      <Lock className="mr-2 h-4 w-4" />
                      Restablecer Contraseña
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#EDF5F0] rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="text-[#4A7C59]" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-[#2B4C3B]">¡Contraseña Restablecida!</h2>
              <p className="text-[#6A736B]">
                Tu contraseña ha sido actualizada exitosamente.
              </p>
              <p className="text-sm text-[#6A736B]">
                Serás redirigido al login en 3 segundos...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}